import { z } from 'zod/v4'

export type ProductLabelContext = {
  id?: unknown
  productID?: unknown
  slug?: unknown
}

const UUID_SCHEMA = z.uuid()
const PRODUCT_NAME_PLACEHOLDERS = new Set(['nom manquant', 'produit sans nom'])

export const normalizeProductText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export const isUuidLikeProductText = (value: string): boolean =>
  UUID_SCHEMA.safeParse(value).success

export const isTechnicalProductText = (
  value: unknown,
  context: ProductLabelContext = {},
): boolean => {
  const normalizedValue = normalizeProductText(value)
  if (!normalizedValue) return true
  if (PRODUCT_NAME_PLACEHOLDERS.has(normalizedValue.toLowerCase())) return true

  if (isUuidLikeProductText(normalizedValue)) return true

  const normalizedCandidates = [
    normalizeProductText(context.id),
    normalizeProductText(context.productID),
    normalizeProductText(context.slug),
  ].filter((candidate): candidate is string => Boolean(candidate))

  return normalizedCandidates.some(
    (candidate) =>
      candidate === normalizedValue ||
      normalizedValue.startsWith(`${candidate}-`),
  )
}

export const pickReadableProductText = (
  candidates: Array<string | undefined>,
  context: ProductLabelContext = {},
  options: { allowTechnicalFallback?: boolean } = {},
): string | undefined => {
  const { allowTechnicalFallback = true } = options
  const normalizedCandidates = candidates.filter(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.length > 0,
  )

  if (normalizedCandidates.length === 0) return undefined

  const readableCandidate = normalizedCandidates.find(
    (candidate) => !isTechnicalProductText(candidate, context),
  )
  if (readableCandidate) return readableCandidate

  return allowTechnicalFallback ? normalizedCandidates[0] : undefined
}
